import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ArrowRight,
    Play,
    PawPrint,
    Clock,
    CheckCircle2,
    Star,
    FileText,
    Shield,
    Award,
    BadgeCheck,
    Heart,
    Zap,
    ChevronRight,
    Download,
    Users,
    Sparkles
} from 'lucide-react'
import { Button } from "@/components/ui/button"

const DemoSection = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeHighlight, setActiveHighlight] = useState(0);
    const [showOverlay, setShowOverlay] = useState(true);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef(null);
    
    // Auto-rotate through highlights
    useEffect(() => {
        if (isPlaying) return; // Don't rotate when video is playing
        
        const interval = setInterval(() => {
            setActiveHighlight((prev) => (prev + 1) % highlights.length);
        }, 4000);
        
        return () => clearInterval(interval);
    }, [isPlaying]);

    // Precarga del video cuando el componente se monta
    useEffect(() => {
        const video = videoRef.current;
        
        if (video) {
            // Evento para cuando los metadatos del video están cargados
            const handleLoadedMetadata = () => {
                setVideoLoaded(true);
            };
            
            // Evento para manejar errores de carga
            const handleError = (e) => {
                console.error("Error loading video:", e);
                setVideoError(true);
            };
            
            // Agregar event listeners
            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            video.addEventListener('error', handleError);
            
            // Intentar precargar el video
            video.load();
            
            // Limpiar event listeners cuando el componente se desmonte
            return () => {
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.removeEventListener('error', handleError);
            };
        }
    }, []);

    const highlights = [
        {
            icon: Clock,
            title: "Rápido y Simple",
            description: "Obtén resultados en menos de 2 minutos",
            color: "from-amber-500 to-orange-500"
        },
        {
            icon: CheckCircle2,
            title: "Preciso y Confiable",
            description: "Tecnología IA con 90% de precisión validada",
            color: "from-green-500 to-emerald-500"
        },
        {
            icon: PawPrint,
            title: "Personalizado",
            description: "Adaptado a la raza y condición de tu mascota",
            color: "from-blue-500 to-cyan-500"
        },
        {
            icon: FileText,
            title: "PDF Descargable",
            description: "Reportes para compartir con tu veterinario",
            color: "from-purple-500 to-indigo-500"
        }
    ];
    
    const testimonials = [
        {
            name: "Carlos Rodríguez",
            pet: "Luna",
            image: "/dogscanuser2.jpeg",
            text: "Detectó problema dermatológico en mi perrita antes de que mostrara síntomas evidentes. El veterinario confirmó el diagnóstico y pudimos tratarla a tiempo."
        },
        {
            name: "Ana Martínez",
            pet: "Rocky",
            image: "/dogscanuser.jpeg",
            text: "La plataforma me alertó sobre problemas nutricionales que no había notado. Seguí las recomendaciones y en un mes mi perro recuperó su energía."
        }
    ];
    
    const handlePlayVideo = () => {
        if (videoRef.current && videoLoaded) {
            videoRef.current.play()
                .then(() => {
                    setIsPlaying(true);
                    setShowOverlay(false);
                })
                .catch(error => {
                    console.error("Error playing video:", error);
                    // Intentar reproducir nuevamente con interacción del usuario
                    const playPromise = videoRef.current.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(() => {
                            // Mostrar mensaje al usuario para que interactúe directamente
                            alert("Por favor, haz clic nuevamente para reproducir el video");
                        });
                    }
                });
        }
    };

    // Función para reintentar la carga del video
    const handleRetryLoad = () => {
        setVideoError(false);
        if (videoRef.current) {
            videoRef.current.load();
        }
    };

    return (
        <section className="py-12 sm:py-16 lg:py-20 relative overflow-hidden isolate" id="demo-section">
            {/* Enhanced Background Pattern */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50/50 to-amber-50" />
                <div className="absolute inset-0 opacity-5" />
                
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <motion.div 
                        className="absolute top-1/4 right-[10%] w-64 h-64 bg-amber-200/30 rounded-full blur-3xl transform-gpu"
                        animate={{
                            scale: [1, 1.1, 1],
                            opacity: [0.3, 0.4, 0.3]
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "linear"
                        }}
                    />
                    <motion.div 
                        className="absolute bottom-1/4 left-[10%] w-64 h-64 bg-orange-200/30 rounded-full blur-3xl transform-gpu"
                        animate={{
                            scale: [1, 1.15, 1],
                            opacity: [0.3, 0.45, 0.3]
                        }}
                        transition={{
                            duration: 10,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "linear"
                        }}
                    />
                </div>
                
                <div className="absolute inset-0 pointer-events-none">
                    <PawPrint className="absolute h-32 w-32 sm:h-48 sm:w-48 lg:h-64 lg:w-64 
                                     -right-8 sm:-right-12 top-8 sm:top-12 text-amber-900/5 rotate-12" />
                    <PawPrint className="absolute h-24 w-24 sm:h-36 sm:w-36 lg:h-48 lg:w-48 
                                     -left-6 sm:-left-8 bottom-6 sm:bottom-8 text-amber-900/5 -rotate-12" />
                </div>
            </div>
    
            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
                {/* Enhanced Header Section */}
                <div className="text-center mb-10 sm:mb-14 lg:mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/80 backdrop-blur-sm 
                               px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm mb-4 sm:mb-6"
                    >
                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                        <span className="text-amber-900 font-medium text-base sm:text-lg">
                            Ver DogScan AI en acción
                        </span>
                    </motion.div>
    
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4"
                    >
                        <span className="block text-amber-900">Mira lo fácil que es</span>
                        <span className="bg-gradient-to-r from-amber-600 to-orange-600 text-transparent bg-clip-text">
                            cuidar la salud de tu mascota
                        </span>
                    </motion.h2>
                    
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-base sm:text-lg lg:text-xl text-amber-700 max-w-3xl mx-auto px-4"
                    >
                        Descubre cómo DogScan AI analiza la salud de tu perro y te brinda 
                        recomendaciones personalizadas respaldadas por veterinarios certificados
                    </motion.p>
                    
                    {/* Trust Indicators */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-wrap justify-center gap-3 sm:gap-5 mt-6"
                    >
                        <div className="flex items-center gap-1.5 text-sm sm:text-base text-amber-800">
                            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                            <span>+10,000 usuarios activos</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm sm:text-base text-amber-800">
                            <BadgeCheck className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                            <span>Certificado por veterinarios</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm sm:text-base text-amber-800">
                            <Award className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                            <span>Plataforma impulsada por Inteligencia Artificial</span>
                        </div>
                    </motion.div>
                </div>
    
                <div className="grid lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-10 items-start">
                    {/* Enhanced Video Container */}
                    <div className="lg:col-span-3">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl 
                                     border border-amber-200/50 bg-gradient-to-br from-amber-50/50 to-orange-50/50"
                        >
                            {/* Video Wrapper */}
                            <div className="relative pb-[56.25%]">
                                {/* Estado de carga */}
                                {!videoLoaded && !videoError && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-amber-50">
                                        <div className="flex flex-col items-center">
                                            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                            <p className="text-amber-800">Cargando video...</p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Estado de error */}
                                {videoError && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-amber-50">
                                        <div className="flex flex-col items-center text-center p-6">
                                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                            </div>
                                            <p className="text-amber-900 font-medium mb-2">No se pudo cargar el video</p>
                                            <p className="text-amber-700 text-sm mb-4">Hubo un problema al cargar el video. Por favor, intenta nuevamente.</p>
                                            <button 
                                                onClick={handleRetryLoad}
                                                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                                            >
                                                Reintentar
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                <video
                                    ref={videoRef}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    poster="/thumbnail.png"
                                    controls={isPlaying}
                                    onClick={handlePlayVideo}
                                    preload="metadata"
                                    playsInline
                                    onLoadedMetadata={() => setVideoLoaded(true)}
                                    onError={() => setVideoError(true)}
                                >
                                    <source src="/dogscanvideo2.mp4" type="video/mp4" />
                                    Tu navegador no soporta el elemento video.
                                </video>
    
                                {/* Enhanced Play Button Overlay */}
                                {showOverlay && videoLoaded && !videoError && (
                                    <motion.div
                                        className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br 
                                               from-amber-900/40 to-orange-900/40 cursor-pointer group"
                                        onClick={handlePlayVideo}
                                        whileHover={{ backgroundColor: 'rgba(120, 53, 15, 0.5)' }}
                                    >
                                        {/* Floating UI Elements */}
                                        <motion.div 
                                            className="absolute top-[20%] left-[15%] bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2
                                                     shadow-lg border border-amber-200/50 transform-gpu"
                                            animate={{
                                                y: [0, -8, 0],
                                                rotate: [0, 1, 0]
                                            }}
                                            transition={{
                                                duration: 4,
                                                repeat: Infinity,
                                                repeatType: "reverse",
                                                ease: "linear"
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                                                <span className="text-sm font-medium text-amber-900">Raza: Golden Retriever</span>
                                            </div>
                                        </motion.div>
                                        
                                        <motion.div 
                                            className="absolute bottom-[25%] right-[20%] bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2
                                                     shadow-lg border border-amber-200/50 transform-gpu"
                                            animate={{
                                                y: [0, 8, 0],
                                                rotate: [0, -1, 0]
                                            }}
                                            transition={{
                                                duration: 5,
                                                repeat: Infinity,
                                                repeatType: "reverse",
                                                ease: "linear"
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Heart className="h-5 w-5 text-red-500" />
                                                <span className="text-sm font-medium text-amber-900">Salud: Excelente</span>
                                            </div>
                                        </motion.div>
                                        
                                        {/* Play Button */}
                                        <motion.div
                                            className="bg-white/90 backdrop-blur-sm rounded-full p-4 sm:p-5
                                                     shadow-lg border border-amber-200/50 group-hover:scale-110 transition-transform"
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <Play className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 fill-amber-600" />
                                        </motion.div>
                                        
                                        <p className="text-white font-medium mt-4 text-lg sm:text-xl">
                                            Ver demostración
                                        </p>
                                    </motion.div>
                                )}
                            </div>
                            
                            {/* Video Caption */}
                            <div className="p-4 sm:p-5 bg-white/80 backdrop-blur-sm">
                                <h3 className="font-semibold text-amber-900 text-lg sm:text-xl mb-1">
                                    Análisis de salud en tiempo real
                                </h3>
                                <p className="text-amber-700 text-sm sm:text-base">
                                    Observa cómo DogScan AI analiza la foto de tu mascota y genera un reporte completo de salud
                                </p>
                            </div>
                        </motion.div>
                        
                        {/* Testimonials */}
                        <div className="mt-6 sm:mt-8">
                            <AnimatePresence mode="wait">
                                {testimonials.map((testimonial, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        className={`bg-white/90 backdrop-blur-sm rounded-xl p-4 sm:p-5 
                                                 shadow-md border border-amber-100/70 ${index === 0 ? 'block' : 'hidden'}`}
                                    >
                                        <div className="flex items-start gap-3 sm:gap-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-amber-200">
                                                    <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-1 mb-1 sm:mb-2">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <Star key={star} className="h-4 w-4 fill-amber-500 text-amber-500" />
                                                    ))}
                                                </div>
                                                <p className="text-sm sm:text-base text-amber-800 italic mb-2">
                                                    "{testimonial.text}"
                                                </p>
                                                <p className="text-xs sm:text-sm text-amber-700 font-medium">
                                                    {testimonial.name} - Dueño de {testimonial.pet}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
    
                    {/* Enhanced Highlights Section */}
                    <div className="lg:col-span-2">
                        {/* Highlights Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 sm:p-5
                                     border border-amber-200/50 shadow-md mb-6"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <Shield className="h-6 w-6 text-amber-600" />
                                <h3 className="text-lg sm:text-xl font-bold text-amber-900">
                                    Beneficios Principales
                                </h3>
                            </div>
                            <p className="text-sm sm:text-base text-amber-700">
                                Nuestra tecnología certificada ofrece resultados precisos y recomendaciones personalizadas para el cuidado óptimo de tu mascota
                            </p>
                        </motion.div>

                        {/* New Highlights Cards Container */}
                        <div className="relative h-[600px] sm:h-[550px] overflow-hidden">
                            <div className="absolute inset-0">
                                <AnimatePresence initial={false} mode="wait">
                                    {highlights.map((highlight, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: 50, position: "absolute", inset: 0 }}
                                            animate={{ 
                                                opacity: activeHighlight === index ? 1 : 0,
                                                x: activeHighlight === index ? 0 : -50,
                                                position: "absolute",
                                                inset: 0,
                                            }}
                                            exit={{ opacity: 0, x: -50, position: "absolute", inset: 0 }}
                                            transition={{ 
                                                type: "tween",
                                                duration: 0.3,
                                                ease: "easeInOut"
                                            }}
                                            style={{ 
                                                position: "absolute",
                                                inset: 0,
                                                pointerEvents: activeHighlight === index ? "auto" : "none"
                                            }}
                                        >
                                            <div className="h-full bg-white/90 backdrop-blur-sm rounded-xl p-6 sm:p-7
                                                        shadow-lg border border-amber-100/70
                                                        transition-shadow duration-300 hover:shadow-xl">
                                                {/* Step Indicator */}
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`bg-gradient-to-br ${highlight.color} 
                                                                    rounded-xl p-3 sm:p-4 text-white`}>
                                                            <highlight.icon className="h-6 w-6 sm:h-7 sm:w-7" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-bold text-lg sm:text-xl text-amber-900">
                                                                {highlight.title}
                                                            </h3>
                                                            <p className="text-amber-600 text-sm sm:text-base">
                                                                {highlight.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className="text-4xl font-bold text-amber-200">
                                                        {(index + 1).toString().padStart(2, '0')}
                                                    </span>
                                                </div>

                                                {/* Content */}
                                                <div className="space-y-6">
                                                    <div className="bg-amber-50/50 rounded-lg p-4 border border-amber-100/50">
                                                        <h4 className="font-semibold text-amber-900 mb-3">
                                                            {index === 0 && "Características Principales"}
                                                            {index === 1 && "Validación y Precisión"}
                                                            {index === 2 && "Personalización Avanzada"}
                                                            {index === 3 && "Gestión de Reportes"}
                                                        </h4>
                                                        <ul className="space-y-3">
                                                            {index === 0 && (
                                                                <>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Interfaz intuitiva y fácil de usar</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Resultados en tiempo real</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Proceso simplificado en 3 pasos</span>
                                                                    </li>
                                                                </>
                                                            )}
                                                            {index === 1 && (
                                                                <>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Validado por veterinarios certificados</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Actualización constante del algoritmo</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Base de datos médica actualizada</span>
                                                                    </li>
                                                                </>
                                                            )}
                                                            {index === 2 && (
                                                                <>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Recomendaciones específicas por raza</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Ajustes según edad y condición física</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Historial de salud personalizado</span>
                                                                    </li>
                                                                </>
                                                            )}
                                                            {index === 3 && (
                                                                <>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Historial completo de análisis</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Compartir fácilmente con tu veterinario</span>
                                                                    </li>
                                                                    <li className="flex items-center gap-2 text-amber-800">
                                                                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                                                        <span>Exportación en múltiples formatos</span>
                                                                    </li>
                                                                </>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>

                                                {/* Navigation Dots */}
                                                <div className="absolute bottom-6 left-0 right-0">
                                                    <div className="flex justify-center items-center gap-2">
                                                        {highlights.map((_, dotIndex) => (
                                                            <button
                                                                key={dotIndex}
                                                                onClick={() => setActiveHighlight(dotIndex)}
                                                                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 
                                                                          ${activeHighlight === dotIndex 
                                                                            ? 'bg-amber-500 w-8' 
                                                                            : 'bg-amber-200 hover:bg-amber-300'}`}
                                                                aria-label={`Go to step ${dotIndex + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4 }}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="mt-6 sm:mt-8 transform-gpu"
                        >
                            <Button
                                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 
                                         text-white px-6 py-6 text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl"
                                onClick={() => window.location.href = '#pricing'}
                            >
                                <div className="flex flex-col items-center w-full">
                                    <span className="text-lg sm:text-xl font-bold mb-1">Prueba DogScan AI ahora</span>
                                    <span className="text-xs sm:text-sm text-white/90">Planes desde $2.00/dia</span>
                                </div>
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </motion.div>
                        
                        {/* Download App */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.5 }}
                            className="flex items-center justify-center gap-2 mt-4 text-amber-700 text-sm"
                        >
                            <Download className="h-4 w-4" />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DemoSection;